import type { ReactNode } from 'react';

import { useFillsProfileCard } from './profile/profileCardSurface';

export interface PreviewRow {
  label: string;
  value: ReactNode;
  /** Spans both columns — for long free text that would wrap badly in the value column. */
  full?: boolean;
  /**
   * Renders the value as a machine value: `font-mono tabular-nums`.
   *
   * **Required on every date and every code**, which is the rule this flag exists to make
   * cheap enough to keep. A wrapper element is still right where the value needs more than
   * the face — a tone, a width, a second class — but for a plain string this is the form,
   * and it is the one the development-time check below can see.
   */
  mono?: boolean;
}

/**
 * A formatted date as `lib/formatDate.ts` renders one: `08 Sep 2026`, `Sep 08, 2026`, each
 * optionally followed by a time. Deliberately narrow — it exists to catch a value that came
 * out of a date formatter and was dropped into a row without `mono`, not to sniff prose.
 */
const LOOKS_LIKE_A_DATE = /^(d{1,2} [A-Z][a-z]{2} d{4}|[A-Z][a-z]{2} d{1,2}, d{4})/;

export interface PreviewSection {
  title?: string;
  rows: PreviewRow[];
}

/**
 * Inline label/value table for detail, overview, and read-view surfaces — reference §14.
 * Not a modal; `<RecordPreviewModal>` wraps this shape when a modal is wanted.
 *
 * Labels and titles are **regular case**, never uppercase — the uppercase/pill chrome is
 * reserved for the audit detail modal (§13), which is the one sanctioned exception.
 *
 * **Dates and codes are `font-mono`, always** — pass `mono` on the row. The component does
 * not infer it from the label, because a label is prose and "From" / "To" / "Unit" say nothing
 * about whether the value is a machine one. What it DOES do is check: in development, a
 * plain-string value that came out of a date formatter and carries no `mono` logs an error
 * naming the row.
 *
 * That check earns its keep. This table shipped with Item monospaced and the From and To
 * directly beneath it not — one screen, one call site, three lines apart. A rule that depends
 * on remembering is a rule that holds until the fourth row.
 */
/** The value, monospaced when the row says so. An absent one never reaches here — it is <Muted />. */
function monoWrap(row: PreviewRow): ReactNode {
  return row.mono ? <span className="font-mono tabular-nums">{row.value}</span> : row.value;
}

export function PreviewTable({
  rows,
  sections,
  valign = 'top',
  variant = 'default',
  labelWidth = 'default',
  className = '',
}: {
  rows?: PreviewRow[];
  /** Wins over `rows` when both are supplied. */
  sections?: PreviewSection[];
  valign?: 'top' | 'middle';
  /**
   * `table-display` is the **Table Display View**: a left label column, tinted and upper-
   * case, against a right value column. It is the field table inside a `<ProfileCard>`.
   * Recorded as a deviation in CLAUDE.md — the default variant keeps labels in regular
   * case per reference §14.
   */
  variant?: 'default' | 'table-display';
  /**
   * How much of the row the label column takes.
   *
   * `default` is 38% of the row — a proportional column, right for a label that is a phrase
   * ("Date of examination", "Status of appointment"). `narrow` is a fixed 5rem, for a key
   * that is a short code rather than a phrase: a survey item number is three characters, and
   * at 38% it sits in a column six times wider than anything in it while the question beside
   * it wraps.
   *
   * **Two named steps, not a free-form class.** A caller passing its own width utility
   * competes with the built-in one at equal specificity, and which wins would depend on
   * Tailwind's stylesheet order rather than on the call site — the rule `<TextField
   * trailingWidth>` already follows.
   */
  labelWidth?: 'default' | 'narrow';
  className?: string;
}) {
  const resolved: PreviewSection[] = sections ?? (rows ? [{ rows }] : []);
  const align = valign === 'top' ? 'align-top' : 'align-middle';
  const tableDisplay = variant === 'table-display';

  /*
    The Table Display label is ordinary text: regular weight, regular case, and the same
    `text-sm` as the value beside it. The TINT is what marks it as a key — that is the whole
    job of the grey column — so the uppercase, the letter-spacing, the 11px and the bold were
    four more signals saying the same thing, and a stack of these cards shouted.

    This puts the label back on reference §14's "labels are regular case, never uppercase",
    so the Table Display View is no longer a deviation from it — only the tint is.

    The default variant keeps `font-semibold`: it has no tint to carry the distinction, so
    weight is the only thing separating its key from its value.
  */
  /*
    The width is one value written once, so the two variants cannot disagree about it — and
    `break-words` rides with it, because a FIXED column can be handed a key longer than it is.
    A survey item number is capped at 16 characters, which at `narrow` has nowhere to break:
    without this it would spill out of the tint and across the value beside it.
  */
  const labelSpan = labelWidth === 'narrow' ? 'w-20' : 'w-[38%]';
  const labelClass = tableDisplay
    ? `${labelSpan} shrink-0 break-words bg-slate-50 px-4 py-3 text-sm font-normal text-slate-500`
    : `${labelSpan} shrink-0 break-words text-xs font-semibold text-slate-500`;

  /*
    The Table Display VALUE is `slate-600`, lighter than the `slate-800` body text this
    product sets a value in elsewhere. Product call, 14 Sep 2026.

    A profile card is a stack of facts nobody is reading word by word — they are scanning
    for the one row they came for — so at full strength a column of thirty values reads as a
    wall. This is the one table where the value recedes; everywhere a value is the thing
    being read rather than scanned, it stays `slate-800`.

    **It still clears AA — 7.56:1 on white**, and it stays darker than the `slate-500`
    label beside it (4.55:1 on the tint), which is what keeps the key/value hierarchy the
    right way round. Recompute both before moving either: the value must not land level
    with the label, or the row stops saying which half is which.
  */
  const valueClass = tableDisplay
    ? 'min-w-0 flex-1 break-words px-4 py-3 text-sm text-slate-600'
    : 'min-w-0 flex-1 break-words text-sm text-slate-700';

  const rowClass = tableDisplay ? 'flex' : 'flex gap-3 px-3 py-2';

  /*
    Filling a `<ProfileCard>`, the card has already drawn the outer edge — so this drops its
    own. Two borders on one boundary is a doubled line, and the square box inside the card
    squares off the radius the card is drawing.

    The row rules stay: `divide-y` is what separates one fact from the next, and that is this
    table's own business rather than the surface's.
  */
  const fillsCard = useFillsProfileCard();

  if (import.meta.env.DEV) {
    for (const section of resolved) {
      for (const row of section.rows) {
        if (!row.mono && typeof row.value === 'string' && LOOKS_LIKE_A_DATE.test(row.value)) {
          console.error(
            '<PreviewTable>: the "' +
              row.label +
              '" row renders a date without mono. Dates and codes are font-mono, ' +
              'always — add mono: true to the row.',
          );
        }
      }
    }
  }

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {resolved.map((section, index) => (
        <section key={section.title ?? index}>
          {section.title ? (
            <h3 className="pb-1.5 text-xs font-semibold text-slate-500">{section.title}</h3>
          ) : null}

          <dl
            className={[
              fillsCard ? '' : 'overflow-hidden rounded-lg border border-slate-200',
              tableDisplay ? 'divide-y divide-slate-200' : 'divide-y divide-slate-100',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {section.rows.map((row) =>
              row.full ? (
                <div key={row.label} className={tableDisplay ? 'px-4 py-3' : 'px-3 py-2'}>
                  <dt
                    className={
                      /* The same label TREATMENT in the full-width layout — tint, case, weight
                         and size move with `labelClass` above, or one row's key renders
                         differently from the rest. The WIDTH does not apply: a full row's
                         label sits over its value rather than beside it, so `labelWidth`
                         has no column here to size. */
                      tableDisplay
                        ? 'pb-1 text-sm font-normal text-slate-500'
                        : 'pb-0.5 text-xs font-semibold text-slate-500'
                    }
                  >
                    {row.label}
                  </dt>
                  <dd
                    className={`min-w-0 break-words text-sm ${tableDisplay ? 'text-slate-800' : 'text-slate-700'}`}
                  >
                    {isEmpty(row.value) ? <Muted /> : monoWrap(row)}
                  </dd>
                </div>
              ) : (
                <div key={row.label} className={`${rowClass} ${align}`}>
                  <dt className={labelClass}>{row.label}</dt>
                  <dd className={valueClass}>{isEmpty(row.value) ? <Muted /> : monoWrap(row)}</dd>
                </div>
              ),
            )}
          </dl>
        </section>
      ))}
    </div>
  );
}

function isEmpty(value: ReactNode): boolean {
  return value === null || value === undefined || value === '';
}

/** Empty values render a muted em-dash, never a blank cell that reads as a layout bug. */
function Muted() {
  return <span className="text-slate-400">—</span>;
}
